import { Component, Watch, Mixins, VModel } from 'vue-property-decorator'
import MixinInterface from '@/_mixins/index'

import { Debounce } from 'lodash-decorators'

import UserAvatar from '@/_components/avatar/user/user.vue'
import ProjectUser from '@/views/Authorization/models/projectUser'
import User from '@/views/User/models/user'

import { minSearchTermLength } from '@/_type/const'
import { emailRegex } from '@/_type/email'
import { parsePhoneNumber } from 'libphonenumber-js'

type UserSelect = ProjectUser | string | null

@Component({
    components: { UserAvatar },
    inheritAttrs: false,
})
export default class UserCombobox extends Mixins<MixinInterface>() {
    @VModel() projectUser!: UserSelect

    minSearchTermLength = minSearchTermLength

    projectUsers: Array<ProjectUser> = []
    projectUsersLoaded = false
    users: Array<User> = []
    usersLoaded = false
    limit = 20
    offset = 0
    searchTerm = ''
    loading = false
    errorMessage = ''

    get isProjectUser() {
        return this.projectUser instanceof ProjectUser ? true : false
    }

    // Validations for combobox
    userExist(isProjectUser) {
        const userExistsErrorMessage = this.$t(
            'fe_validation.error_projectUserExistOnProject'
        ).toString()

        if (isProjectUser) {
            return userExistsErrorMessage
        } else if (
            typeof this.projectUser === 'string' &&
            this.searchedUsers.find((user) => user.primaryEmail === this.projectUser)
        ) {
            return userExistsErrorMessage
        }
        return true
    }

    testEmail(email: string) {
        return emailRegex.test(email.trim())
            ? true
            : this.getTranslation('fe_validation.error_valid_email').toString()
    }

    get searchedUsers() {
        return [...this.projectUsers, ...this.users]
    }

    // Check if provided input is an email and add this email to the combobox dropdown list
    get isEmail() {
        let re = /\S+@\S+\.\S+/
        return (
            re.test(this.searchTerm) &&
            this.searchedUsers.findIndex((item) => item['primaryEmail'] == this.searchTerm) == -1
        )
    }

    get email() {
        return this.isEmail ? this.searchTerm : null
    }

    isValidEmail() {
        if (!this.email) return true
        let res: string | boolean = true
        if (this.email && this.email.length > 2) {
            if (this.email.includes('@')) {
                res = this.testEmail(this.email)
            }
        } else {
            res = this.$t('fe_validation.error_required').toString()
        }

        return res
    }

    get isPhone() {
        return (
            this.searchedUsers.length == 0 &&
            /^[+\d]/.test(this.searchTerm) &&
            !/@/.test(this.searchTerm) &&
            /^[^a-zA-Z]*$/.test(this.searchTerm)
        )
    }

    get phone() {
        return this.isPhone ? this.searchTerm : null
    }

    get isValidPhone() {
        if (!this.phone) return true

        let isValidPhoneNumber = false
        try {
            const result = parsePhoneNumber(this.phone)
            isValidPhoneNumber = result.isValid()
        } catch {
            // ignore parsing error (for example TOO_SHORT)
        }

        return isValidPhoneNumber ? true : this.$t('fe_validation.error_required').toString()
    }

    get usersCombined() {
        return [
            ...(this.email ? [this.email] : []),
            ...(this.phone ? [this.phone] : []),
            ...this.searchedUsers,
        ]
    }

    // Searching for users first in the authorization api and then in user api
    debounceisOn = false

    @Watch('searchTerm')
    handleDebounce() {
        this.debounceisOn = true
        this.projectUsers = []
        this.users = []
        this.errorMessage = ''
        this.loading = true
    }

    @Watch('searchTerm')
    @Debounce(800)
    filterUsers() {
        if (this.searchTerm && this.searchTerm.length >= this.minSearchTermLength) {
            this.offset = 0
            this.projectUsersLoaded = false
            this.usersLoaded = false
            this.handleUsers()
        } else {
            this.loading = false
            this.debounceisOn = false
        }
    }

    async searchProjectUsers() {
        let query = {
            include: ['displayContractor', 'displayContractor.company', 'user', 'roles'],
            filters: {
                'filter[userKindIfConfirmed]': 'normal',
                'filter[project]': this.currentProjectId,
                ...(this.searchTerm &&
                    this.searchTerm.length >= this.minSearchTermLength && {
                        'filter[searchTerm]': this.searchTerm,
                    }),
                'page[offset]': this.offset,
                'page[limit]': this.limit,
            },
        }

        let res = await ProjectUser.searchProjectUser(query)

        this.projectUsers = [...this.projectUsers, ...res]

        if (!this.searchTerm) this.projectUsers = this.projectUsers.filter((user) => user.hasRoles)

        return res
    }

    async searchUsers() {
        let query = {
            include: ['employer'],
            filters: {
                'filter[kind]': 'normal',
                ...(this.searchTerm &&
                    this.searchTerm.length >= this.minSearchTermLength && {
                        'filter[searchTerm]': this.searchTerm,
                    }),

                'filter[isEnabled]': true,
                'page[offset]': this.offset,
                'page[limit]': this.limit,
            },
        }

        let res = await User.searchUsers(query)
        let filteredRes = res.filter((user: User) => {
            let index = this.projectUsers.findIndex((projectUser: any) => {
                return projectUser.userId == user._id
            })
            return index == -1
        })

        this.users = [...this.users, ...filteredRes]
        return res
    }

    async handleUsers() {
        if (
            this.currentProjectId &&
            (!this.searchTerm || this.searchTerm.length >= this.minSearchTermLength)
        ) {
            this.loading = true
            if (!this.projectUsersLoaded) {
                let res = await this.searchProjectUsers()
                if (res) {
                    if (res.length == this.limit) {
                        this.offset += this.limit
                    } else {
                        this.projectUsersLoaded = true
                        this.offset = 0
                        this.handleUsers()
                    }
                } else {
                    this.projectUsersLoaded = true
                }
            } else {
                if (!this.usersLoaded) {
                    if (this.searchTerm && this.searchTerm.length >= this.minSearchTermLength) {
                        let res = await this.searchUsers()
                        if (res) {
                            if (res.length == this.limit) {
                                this.offset += this.limit
                            } else {
                                this.usersLoaded = true
                            }
                        } else {
                            this.usersLoaded = true
                        }
                    }
                }
            }
        }
        this.loading = false
        this.debounceisOn = false
    }

    async loadMore(entries, observer, isIntersecting) {
        if (isIntersecting) {
            await this.handleUsers()
        }
    }
}
